import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {QueryBlockFilter, QueryBlockItem, QueryBlockOperator} from '../models/query-block';
import {ResearchKeywordDTO} from '../dto/research-keywords';
import {ResearchSource} from '../dto/research-paper';

@Injectable({
  providedIn: 'root'
})
export class QueryHelperService {
  private dragStarted = new Subject<boolean>();

  readonly dragStarted$ = this.dragStarted.asObservable();

  setDragStarted(draggingStarted: boolean) {
    this.dragStarted.next(draggingStarted);
  }

  getBlockColor(blockId: string): string {
    const colors = [
      '#FFD1DC', // Rosa
      '#A7C7E7', // Hellblau
      '#C1E1C1', // Hellgrün
      '#FFDA9E', // Hellorange
      '#C5A3FF', // Helllila
      '#FFB347', // Hellorange
      '#87CEEB', // Himmelblau
      '#98FB98', // Pastellgrün
      '#FFA07A', // Lachsrosa
      '#FFFACD', // Pastellgelb
      '#B0E0E6', // Pulverblau
      '#F0E68C', // Khaki
      '#E6E6FA', // Lavendel
      '#FFDAB9', // Pfirsich
      '#D8BFD8'  // Distel
    ];

    let hash = 0;
    for (let i = 0; i < blockId.length; i++) {
      hash = blockId.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  }

  public buildQueryString(item: QueryBlockItem, source: ResearchSource): string {
    if (item.keyword && (!item.children || item.children.length === 0)) {
      const synonyms = [item.keyword.keyword, ...item.keyword.synonyms]
        .map(s => `"${s}"`);

      const query = synonyms.join(` OR `);
      if (item.filter && item.filter !== QueryBlockFilter.None) {
        const filterString = this.getFilterString(item.filter, source);
        switch (source) {
          case ResearchSource.PUBMED: {
            const query = synonyms
              .map(s => `${s}[${filterString}]`)
              .join(` OR `);

            return `(${query})`;
          }
          case ResearchSource.SCOPUS: {
            const query = synonyms.join(` OR `);
            return `${filterString}(${query})`;
          }
        }
      }
      return `(${query})`;
    }

    if (item.children && item.children.length > 0) {
      const childQueries = item.children.map(child => this.buildQueryString(child, source))
        .filter(query => query && query.length > 0);

      if (childQueries.length === 0) return '';
      if (childQueries.length === 1) return childQueries[0];

      const operator = item.operator || QueryBlockOperator.AND;
      let joinedQuery = childQueries.join(` ${operator} `);
      if (source == ResearchSource.SEMANTIC_SCHOLAR) {
        joinedQuery = joinedQuery
          .replaceAll(QueryBlockOperator.AND, "+")
          .replaceAll(QueryBlockOperator.OR, "|")
      }
      return `(${joinedQuery})`;
    }

    return '';
  }

  private getFilterString(filter: QueryBlockFilter, source: ResearchSource) {
    switch (source) {
      case ResearchSource.PUBMED: {
        switch (filter) {
          case QueryBlockFilter.All:
            return "All Fields";
          case QueryBlockFilter.MESH:
            return "MeSH Terms";
          case QueryBlockFilter.TITLE_ABSTRACT:
            return "Title/Abstract";
        }
        break;
      }
      case ResearchSource.SCOPUS: {
        switch (filter) {
          case QueryBlockFilter.All:
            return "ALL";
          case QueryBlockFilter.MESH:
            return "ALL";
          case QueryBlockFilter.TITLE_ABSTRACT:
            return "TITLE-ABS-KEY";
        }
        break;
      }
    }
    return "";
  }


  public constructQuery(query: string, keywords: ResearchKeywordDTO[], source: ResearchSource): QueryBlockItem {
    const rootItem = new QueryBlockItem();

    if (!query || !keywords?.length) return rootItem;

    // Bereinige den Query-String
    query = query.replace(/\(\s+/g, '(').replace(/\s+\)/g, ')').replace(/\s+/g, ' ').trim();

    try {
      // Parse den Query-String unter Berücksichtigung des source-Parameters
      rootItem.children = this.parseExpression(query, keywords, source);
      if (rootItem.children.length === 1 && !rootItem.keyword && rootItem.children[0].children.length > 1) {
        return rootItem.children[0];
      }
      return rootItem;
    } catch (error) {
      console.error('Fehler beim Parsen der Query:', error);
      return new QueryBlockItem({ children: [] });
    }
  }

  private parseExpression(expression: string, keywords: ResearchKeywordDTO[], source: ResearchSource): QueryBlockItem[] {
    expression = expression.trim();

    if (!expression) return [];

    // Normalize Semantic Scholar operators (+ → AND, | → OR) so the rest of the parser handles them uniformly
    if (source === ResearchSource.SEMANTIC_SCHOLAR) {
      expression = expression.replace(/ \+ /g, ' AND ').replace(/ \| /g, ' OR ');
    }

    // SCOPUS-spezifische Filtererkennung: Prüfe, ob ein Filterpräfix vorliegt, z. B. "TITLE-ABS-KEY(...)"
    if (source === ResearchSource.SCOPUS) {
      const scopusFilterRegex = /^(ALL|TITLE-ABS-KEY)\((.*)\)$/;
      const scopusMatch = expression.match(scopusFilterRegex);
      if (scopusMatch) {
        const filterPrefix = scopusMatch[1];
        const innerExpression = scopusMatch[2].trim();
        let filter: QueryBlockFilter = QueryBlockFilter.All;
        if (filterPrefix === "TITLE-ABS-KEY") {
          filter = QueryBlockFilter.TITLE_ABSTRACT;
        }
        const items = this.parseExpression(innerExpression, keywords, source);
        this.assignFilterRecursively(items, filter);
        return items;
      }
    }

    // Entferne äußere Klammern
    expression = this.removeOuterBrackets(expression);
    if (!expression) return [];

    // AND-Ausdrücke auf oberster Ebene
    if (this.containsOperatorOutsideBrackets(expression, 'AND')) {
      const parts = this.splitByTopLevelOperator(expression, 'AND');
      if (parts.length > 1) {
        const andItem = new QueryBlockItem({
          operator: QueryBlockOperator.AND,
          children: []
        });
        for (const part of parts) {
          const parsedItems = this.parseExpression(part, keywords, source);
          andItem.children.push(...parsedItems);
        }
        return [andItem];
      }
    }

    // OR-Ausdrücke auf oberster Ebene
    if (this.containsOperatorOutsideBrackets(expression, 'OR')) {
      const parts = this.splitByTopLevelOperator(expression, 'OR');
      if (parts.length > 1) {
        // Hier wird findMatchingKeyword aufgerufen, das nun je nach Source (PubMed/Scopus)
        // den Filter extrahiert und zuweist
        const foundKeywords = this.findMatchingKeyword(parts, keywords, source);
        if (foundKeywords && foundKeywords.length > 0) {
          const parsedFoundKeywords = foundKeywords
            .filter(key => key && key.keyword && key.keyword.length > 0)
            .map(key =>
              new QueryBlockItem({ keyword: key, filter: key.filter })
            );
          if (parsedFoundKeywords.length > 1) {
            return [new QueryBlockItem({
              operator: QueryBlockOperator.OR,
              children: parsedFoundKeywords
            })];
          } else {
            return parsedFoundKeywords;
          }
        }
        const orItem = new QueryBlockItem({
          operator: QueryBlockOperator.OR,
          children: []
        });
        for (const part of parts) {
          const parsedItems = this.parseExpression(part, keywords, source);
          orItem.children.push(...parsedItems);
        }
        return [orItem];
      }
    }

    // Falls es sich um einen verschachtelten Ausdruck handelt
    if (expression.includes('(')) {
      const stripped = this.removeOuterBrackets(expression);
      if (stripped !== expression) {
        return this.parseExpression(stripped, keywords, source);
      }
      // removeOuterBrackets made no progress — fall through to keyword handling to avoid infinite recursion
    }
    {
      // Einfaches Keyword
      // Für PubMed könnte das Keyword bereits Filterinformationen enthalten, z. B. "Keyword"[All Fields]
      if (source === ResearchSource.PUBMED) {
        const pubmedRegex = /^"(.+)"\s*\[([^\]]+)\]$/;
        const match = expression.match(pubmedRegex);
        if (match) {
          const keywordText = match[1].trim();
          const filterText = match[2].trim();
          let filter: QueryBlockFilter = QueryBlockFilter.None;
          if (filterText === "All Fields") {
            filter = QueryBlockFilter.All;
          } else if (filterText === "MeSH Terms") {
            filter = QueryBlockFilter.MESH;
          } else if (filterText === "Title/Abstract") {
            filter = QueryBlockFilter.TITLE_ABSTRACT;
          }
          for (const kw of keywords) {
            if (kw.keyword === keywordText) {
              return [new QueryBlockItem({ keyword: kw, filter })];
            }
          }
          return [new QueryBlockItem({ keyword: { keyword: keywordText, synonyms: [] }, filter } as any)];
        }
      }
      // Normales Keyword ohne Filterannotation
      const trimmedExpression = expression.replace(/"/g, '').trim();
      for (const kw of keywords) {
        if (kw.keyword === trimmedExpression) {
          return [new QueryBlockItem({ keyword: kw })];
        }
      }
      return [new QueryBlockItem({ keyword: { keyword: trimmedExpression, synonyms: [] } } as any)];
    }
  }

  /**
   * Modifizierte findMatchingKeyword-Methode, die bei PubMed die Filterinformation extrahiert.
   * Für PubMed werden Terme im Format "Keyword"[Filter] verarbeitet, während bei Scopus nur die Terme
   * bereinigt werden.
   */
  private findMatchingKeyword(terms: string[], keywords: ResearchKeywordDTO[], source: ResearchSource): (ResearchKeywordDTO & { filter?: QueryBlockFilter })[] {
    const cleanedTerms: { text: string, filter?: QueryBlockFilter }[] = [];

    if (source === ResearchSource.PUBMED) {
      const pubmedRegex = /^"(.+)"\s*\[([^\]]+)\]$/;
      for (const term of terms) {
        const match = term.match(pubmedRegex);
        if (match) {
          const text = match[1].trim();
          const filterText = match[2].trim();
          let filter: QueryBlockFilter = QueryBlockFilter.None;
          if (filterText === "All Fields") {
            filter = QueryBlockFilter.All;
          } else if (filterText === "MeSH Terms") {
            filter = QueryBlockFilter.MESH;
          } else if (filterText === "Title/Abstract") {
            filter = QueryBlockFilter.TITLE_ABSTRACT;
          }
          cleanedTerms.push({ text, filter });
        } else {
          cleanedTerms.push({ text: term.replace(/"/g, '').trim() });
        }
      }
    } else { // SCOPUS oder andere Quellen
      for (const term of terms) {
        cleanedTerms.push({ text: term.replace(/"/g, '').trim() });
      }
    }

    const matchedKeywords: (ResearchKeywordDTO & { filter?: QueryBlockFilter })[] = [];

    // Durchlaufe alle bekannten Keywords und deren Synonyme
    for (const keyword of keywords) {
      const allTerms = [keyword.keyword, ...keyword.synonyms];
      const foundTerms = allTerms.map(t => cleanedTerms.find(ct => ct.text === t));
      const allTermsPresent = foundTerms.every(ft => ft !== undefined);
      if (allTermsPresent) {
        // Nutze den Filter des ersten gefundenen Terms, falls vorhanden
        const filter = foundTerms[0]?.filter;
        matchedKeywords.push({ ...keyword, filter });
        // Entferne diese Terme aus cleanedTerms
        for (const t of allTerms) {
          const index = cleanedTerms.findIndex(ct => ct.text === t);
          if (index > -1) {
            cleanedTerms.splice(index, 1);
          }
        }
      }
    }
    // Füge übrige Terme als einzelne Keywords hinzu
    for (const ct of cleanedTerms) {
      if (!ct.text || ct.text.length === 0) continue;
      matchedKeywords.push({ keyword: ct.text, synonyms: [], filter: ct.filter } as any);
    }
    return matchedKeywords;
  }

  /**
   * Verteilt den angegebenen Filter rekursiv auf alle Blatt-Knoten (Keywords),
   * sofern dort noch kein Filter gesetzt ist.
   */
  private assignFilterRecursively(items: QueryBlockItem[], filter: QueryBlockFilter): void {
    for (const item of items) {
      if (item.children && item.children.length > 0) {
        this.assignFilterRecursively(item.children, filter);
      } else {
        if (!item.filter || item.filter === QueryBlockFilter.None) {
          item.filter = filter;
        }
      }
    }
  }

  private containsOperatorOutsideBrackets(expression: string, operator: string): boolean {
    let bracketCount = 0;
    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];
      if (char === '(') bracketCount++;
      else if (char === ')') bracketCount--;
      if (bracketCount === 0 &&
        i + operator.length + 2 <= expression.length &&
        expression.substring(i, i + operator.length + 2) === ` ${operator} `) {
        return true;
      }
    }
    return false;
  }

  private splitByTopLevelOperator(expression: string, operator: string): string[] {
    const result: string[] = [];
    let bracketCount = 0;
    let currentPart = '';
    let i = 0;
    while (i < expression.length) {
      const char = expression[i];
      if (char === '(') bracketCount++;
      else if (char === ')') bracketCount--;
      if (bracketCount === 0 &&
        i + operator.length + 2 <= expression.length &&
        expression.substring(i, i + operator.length + 2) === ` ${operator} `) {
        result.push(currentPart);
        currentPart = '';
        i += operator.length + 1;
      } else {
        currentPart += char;
      }
      i++;
    }
    if (currentPart) result.push(currentPart);
    return result;
  }

  private removeOuterBrackets(text: string): string {
    text = text.trim();
    while (text.startsWith('(') && text.endsWith(')')) {
      const withoutBrackets = text.substring(1, text.length - 1).trim();
      let count = 0;
      let valid = true;
      for (let i = 0; i < withoutBrackets.length; i++) {
        if (withoutBrackets[i] === '(') count++;
        else if (withoutBrackets[i] === ')') {
          count--;
          if (count < 0) {
            valid = false;
            break;
          }
        }
      }
      if (valid && count === 0) {
        text = withoutBrackets;
      } else {
        break;
      }
    }
    return text;
  }
}
